import { makeAutoObservable } from 'mobx';
import { AppConfigItem } from '../../models/AppConfig'; 
import { AppConfigApi } from '../../apis/AppConfig';
import { AliOssUtil } from '../../utils/AliOssUtil';
import { OssScene } from '../../global/OssConfig';
export class AppConfigManagementPageModel {
  configs: AppConfigItem[] = [];
  loading = false;
  searchText = '';
  editingConfig: AppConfigItem | null = null;
  isEditModalVisible = false;
  isDeleteModalVisible = false;
  deletingConfig: AppConfigItem | null = null;
  uploadingResource = false;

  constructor() {
    makeAutoObservable(this);
  }

  // 加载配置列表
  async loadConfigs() {
    this.loading = true;
    try {
      const configs = await AppConfigApi.getAllConfigs();
      this.configs = configs;
    } catch (error) {
      console.error('加载配置列表失败:', error);
    } finally {
      this.loading = false;
    }
  }

  // 设置搜索文本
  setSearchText(text: string) {
    this.searchText = text;
  }

  // 获取过滤后的配置列表
  get filteredConfigs() {
    if (!this.searchText) {
      return this.configs;
    }
    
    const searchLower = this.searchText.toLowerCase();
    return this.configs.filter(config =>
      (config.name || '').toLowerCase().includes(searchLower) ||
      (config.key || '').toLowerCase().includes(searchLower) ||
      (config.description || '').toLowerCase().includes(searchLower)
    );
  }

  // 显示编辑弹框
  showEditModal(config: AppConfigItem) {
    this.editingConfig = { ...config };
    this.isEditModalVisible = true;
  }

  // 隐藏编辑弹框
  hideEditModal() {
    this.isEditModalVisible = false;
    this.editingConfig = null;
  }

  // 保存配置项
  async saveConfig() {
    if (!this.editingConfig) return;

    if (this.editingConfig.id) {
      const success = await AppConfigApi.updateConfigItem(this.editingConfig);
      if (!success) {
        throw new Error('更新配置失败');
      }
      const index = this.configs.findIndex(c => c.id === this.editingConfig!.id);
      if (index !== -1) {
        this.configs[index] = { ...this.editingConfig };
      }
    } else {
      const newId = await AppConfigApi.addConfigItem(this.editingConfig);
      this.editingConfig.id = newId.toString();
      this.configs.push({ ...this.editingConfig });
    }
    this.hideEditModal();
  }

  // 显示删除确认弹框
  showDeleteModal(config: AppConfigItem) {
    this.deletingConfig = config;
    this.isDeleteModalVisible = true;
  }

  // 隐藏删除确认弹框
  hideDeleteModal() {
    this.isDeleteModalVisible = false;
    this.deletingConfig = null;
  }

  // 删除配置项
  async confirmDelete() {
    if (!this.deletingConfig) return;
    
    try {
      const success = await AppConfigApi.deleteConfigItem(this.deletingConfig.id);
      if (success) {
        this.configs = this.configs.filter(c => c.id !== this.deletingConfig!.id);
        this.hideDeleteModal();
      }
    } catch (error) {
      console.error('删除配置失败:', error);
    }
  }

  // 更新编辑中的配置项
  updateEditingConfig(field: keyof AppConfigItem, value: string) {
    if (this.editingConfig) {
      (this.editingConfig as any)[field] = value;
    }
  }

  async uploadConfigResource(file: File): Promise<string> {
    this.uploadingResource = true;
    try {
      return await AliOssUtil.uploadFile(file, OssScene.appConfig);
    } finally {
      this.uploadingResource = false;
    }
  }
} 